import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  connect() {
    this.formatValue(this.element);
  }

  formatValue(input) {
    const cursorPosition = this.#getCursorPosition(input);
    const digitCountBeforeCursor = this.#getDigitCountBeforePosition(input.value, cursorPosition);
    const formatted = this.#formatEIN(input.value);

    input.value = formatted;

    const newPosition = this.#calculateCursorPosition(formatted, digitCountBeforeCursor);
    this.#setCursorPosition(input, newPosition);
  }

  input(event) {
    this.formatValue(event.target)
  }

  keydown(event) {
    const input = event.target;
    const key = event.key;
    const cursorPosition = this.#getCursorPosition(input);
    const value = input.value;

    const allowedKeys = [
      'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Home', 'End'
    ];

    if (allowedKeys.includes(key)) {
      if (key === 'Backspace' && cursorPosition > 0) {
        const charBeforeCursor = value[cursorPosition - 1];
        if (charBeforeCursor === '-') {
          event.preventDefault();
          const digitCountBeforeCursor = this.#getDigitCountBeforePosition(value, cursorPosition);
          const combined = this.#splitAndCombineDigits(value, digitCountBeforeCursor, -1, 0);
          input.value = this.#formatEIN(combined);

          const newDigitCount = digitCountBeforeCursor - 1;
          const newPosition = this.#calculateCursorPosition(input.value, newDigitCount);
          this.#setCursorPosition(input, newPosition);
          return;
        }
      }

      if (key === 'Delete' && cursorPosition < value.length) {
        const charAtCursor = value[cursorPosition]
        if (charAtCursor === '-') {
          event.preventDefault();
          const digitCountBeforeCursor = this.#getDigitCountBeforePosition(value, cursorPosition);
          const combined = this.#splitAndCombineDigits(value, digitCountBeforeCursor, 0, 1);
          input.value = this.#formatEIN(combined);

          const newDigitCount = digitCountBeforeCursor;
          const newPosition = this.#calculateCursorPosition(input.value, newDigitCount);
          this.#setCursorPosition(input, newPosition);
          return;
        }
      }
      return;
    }

    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }

    if (!/^\d$/.test(key)) {
      event.preventDefault()
      return;
    }

    const currentDigits = this.#extractDigits(input.value).length;
    const selectedDigits = this.#getSelectedDigitCount(input);
    const digitsAfterInput = currentDigits - selectedDigits + 1;

    if (digitsAfterInput > 9) {
      event.preventDefault()
      return;
    }
  }

  paste(event) {
    event.preventDefault();

    const input = event.target;
    const cursorPosition = this.#getCursorPosition(input);
    const digitCountBeforeCursor = this.#getDigitCountBeforePosition(input.value, cursorPosition);

    const paste = (event.clipboardData || window.clipboardData).getData('text');
    let pastedDigits = this.#extractDigits(paste);

    const combined = this.#splitAndCombineDigits(input.value, digitCountBeforeCursor, 0, 0, pastedDigits)

    input.value = this.#formatEIN(combined);

    const newDigitCount = digitCountBeforeCursor + pastedDigits.length;
    const newPosition = this.#calculateCursorPosition(input.value, newDigitCount);

    setTimeout(() => {
      this.#setCursorPosition(input, newPosition)
    }, 0)
  }


  // Private utility methods
  #extractDigits(value) {
    return value.replace(/\D/g, '');
  }

  #limitToMaxDigits(digits, maxDigits = 9) {
    return digits.length > maxDigits ? digits.slice(0, maxDigits) : digits
  }

  #splitAndCombineDigits(value, digitCountBeforeCursor, beforeOffset = 0, afterOffset = 0, newDigits = '') {
    const digits = this.#extractDigits(value);
    const beforeCursor = digits.slice(0, digitCountBeforeCursor + beforeOffset);
    const afterCursor = digits.slice(digitCountBeforeCursor + afterOffset);
    const combined = this.#limitToMaxDigits(beforeCursor + newDigits + afterCursor);
    return combined;
  }

  #formatEIN(value) {
    const digits = this.#limitToMaxDigits(this.#extractDigits(value));

    // Format as XX-XXXXXXX
    let formatted = '';
    if (digits.length > 0) {
      formatted = digits.slice(0, 2);
    }
    if (digits.length > 2) {
      formatted += '-' + digits.slice(2, 9);
    }

    return formatted;
  }

  #getCursorPosition(input) {
    return input.selectionStart || 0;
  }

  #getSelectedDigitCount(input) {
    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const selectedText = input.value.slice(start, end);
    return this.#extractDigits(selectedText).length;
  }

  #setCursorPosition(input, position) {
    input.setSelectionRange(position, position);
  }

  #getDigitCountBeforePosition(value, position) {
    return this.#extractDigits(value.slice(0, position)).length;
  }

  #calculateCursorPosition(formattedValue, targetDigitCount) {
    let newPosition = 0;
    let digitCount = 0;

    for (let i = 0; i < formattedValue.length; i++) {
      if (/\d/.test(formattedValue[i])) {
        digitCount++
        if (digitCount === targetDigitCount) {
          newPosition = i + 1;
          break
        }
      }
    }

    if (targetDigitCount >= this.#extractDigits(formattedValue).length) {
      newPosition = formattedValue.length;
    }

    return newPosition;
  }
}
